/**
 * Every ranking an item has received, and the standing of each.
 *
 * An item has no rank of its own (canon 03, `items`: "Priority is contextual").
 * During a cycle it is ranked many times — by each session that ranks it, then
 * in the list a track puts forward — and none of those is official. It acquires
 * an official rank only when it becomes part of a Plan of Record
 * (`item_official_ranks`, migration 819). "Ranking informs priority; it does
 * not establish it." (glossary, Accepted Priorities)
 *
 * This file is the pure shaping: raw rows in, one ordered list out. No I/O, so
 * it is unit-tested with fixtures (itemRankings.test.ts).
 */

/** Where a ranking stands. Only `official` is official. */
export type RankStanding =
  /** The current Plan of Record for its cycle. */
  | 'official'
  /** From an authorized plan that is not in force yet: "Starts on <date>", or "Start date pending" (922). */
  | 'starts_later'
  /** Was official; a newer Plan of Record has replaced it. */
  | 'replaced'
  /** A list the decision authority has agreed, not yet part of a Plan of Record. */
  | 'agreed'
  /** A list put forward and still under consideration. */
  | 'proposed'
  /** One session's result. Informational. */
  | 'session';

export interface ItemRanking {
  /** Stable key for React. */
  key: string;
  standing: RankStanding;
  /** Null when the item is on the list but was given no position. */
  rank: number | null;
  /** True when other items share this rank. Never true for `official`. */
  tied: boolean;
  /** How many items the list ranked, when known. */
  outOf: number | null;
  /** False = below the line. Null = the list draws no line. */
  aboveTheLine: boolean | null;
  /** The name of the plan, list or session that produced the rank. */
  sourceName: string;
  /** In-app link to the source, when there is one. */
  sourceHref: string | null;
  /** ISO timestamp of when the rank was produced. */
  at: string | null;
  /** Who authorized it — set for official and replaced ranks. */
  by: string | null;
  /** For `starts_later`: the plan's start date, or null when its start date is pending. */
  startsOn?: string | null;
  /** For official, starts_later and replaced ranks: the plan (plan_cycles.id). */
  planCycleId?: string;
  /**
   * For those ranks: the prioritization cycle the plan was built from (the cycle
   * of its item pool or published list, as the database resolves it), or null
   * when the database ties the plan to no cycle.
   */
  cycleId?: string | null;
  /**
   * What produced the rank, so its call can be drawn beside it (the Decision
   * that wrote it: see src/lib/call/rankingDecision.ts). A session rank names
   * its session; a list rank its list; a rank of record the plan-of-record
   * snapshot and plan version the authorization described.
   */
  sessionId?: string;
  prioritySetId?: string;
  planOfRecordSnapshotId?: string | null;
  planVersionId?: string | null;
}

// ── Raw shapes (what the service reads) ─────────────────────────────────────

export interface RawOfficialRank {
  id: string;
  rank: number | null;
  above_the_line: boolean;
  plan_cycle_id: string;
  authorized_at: string;
  authorized_by: string | null;
  plan_cycle_name: string | null;
  /** The prioritization cycle the plan was built from (`plan_cycle_governing_cycle`, mig 843), or null. */
  cycle_id?: string | null;
  /** True when the Plan of Record that produced this row has been replaced. */
  superseded: boolean;
  /** True when its plan is authorized but not in force yet (plan_force_states = starts_later). */
  starts_later?: boolean;
  /** That plan's start date; null = pending. */
  starts_on?: string | null;
  /** The authorization that wrote this rank (819): its snapshot and plan version. */
  plan_of_record_snapshot_id?: string | null;
  plan_version_id?: string | null;
}

export interface RawPrioritySet {
  id: string;
  name: string;
  state: string;
  track_id: string;
  ranked_item_ids: string[] | null;
  cut_line_position: number | null;
  proposed_at: string | null;
  ratified_at: string | null;
  published_at: string | null;
}

export interface RawSessionResult {
  id: string;
  session_id: string;
  rank: number;
  created_at: string;
  session_name: string | null;
  /** How many items this session ranked. */
  ranked_count: number | null;
  /** How many items in this session hold this same rank (1 = not tied). */
  same_rank_count: number | null;
}

export interface ItemRankingSources {
  official: readonly RawOfficialRank[];
  prioritySets: readonly RawPrioritySet[];
  sessionResults: readonly RawSessionResult[];
}

// ── Shaping ──────────────────────────────────────────────────────────────────

const STANDING_ORDER: Record<RankStanding, number> = {
  official: 0,
  starts_later: 1,
  agreed: 2,
  proposed: 3,
  session: 4,
  replaced: 5,
};

function byNewest(a: ItemRanking, b: ItemRanking): number {
  return (b.at ?? '').localeCompare(a.at ?? '');
}

/**
 * A priority list's standing. `superseded` sets are dropped by the caller: a
 * list that was replaced before it ever reached a Plan of Record says nothing
 * a reader needs.
 */
function prioritySetStanding(state: string): RankStanding | null {
  if (state === 'ratified' || state === 'published') return 'agreed';
  if (state === 'proposed') return 'proposed';
  return null;
}

/** One list, most authoritative first; newest first within a standing. */
export function buildItemRankings(itemId: string, sources: ItemRankingSources): ItemRanking[] {
  const out: ItemRanking[] = [];

  for (const r of sources.official) {
    out.push({
      key: `official:${r.id}`,
      standing: r.superseded ? 'replaced' : r.starts_later ? 'starts_later' : 'official',
      rank: r.rank,
      tied: false,
      outOf: null,
      aboveTheLine: r.above_the_line,
      sourceName: r.plan_cycle_name?.trim() || 'Plan of Record',
      sourceHref: `/planning/plans/${r.plan_cycle_id}`,
      planCycleId: r.plan_cycle_id,
      cycleId: r.cycle_id ?? null,
      planOfRecordSnapshotId: r.plan_of_record_snapshot_id ?? null,
      planVersionId: r.plan_version_id ?? null,
      at: r.authorized_at,
      by: r.authorized_by,
      ...(r.starts_later && !r.superseded ? { startsOn: r.starts_on ?? null } : {}),
    });
  }

  for (const ps of sources.prioritySets) {
    const standing = prioritySetStanding(ps.state);
    if (!standing) continue;
    const ids = ps.ranked_item_ids ?? [];
    const index = ids.indexOf(itemId);
    if (index < 0) continue;
    const rank = index + 1;
    out.push({
      key: `set:${ps.id}`,
      standing,
      rank,
      tied: false,
      outOf: ids.length,
      aboveTheLine: ps.cut_line_position == null ? null : rank <= ps.cut_line_position,
      sourceName: ps.name?.trim() || 'Priority list',
      sourceHref: `/prioritization/tracks/${ps.track_id}`,
      prioritySetId: ps.id,
      at: ps.published_at ?? ps.ratified_at ?? ps.proposed_at,
      by: null,
    });
  }

  for (const s of sources.sessionResults) {
    out.push({
      key: `session:${s.id}`,
      standing: 'session',
      rank: s.rank,
      tied: (s.same_rank_count ?? 1) > 1,
      outOf: s.ranked_count,
      aboveTheLine: null,
      sourceName: s.session_name?.trim() || 'Session',
      sourceHref: `/prioritization/sessions/${s.session_id}/results`,
      sessionId: s.session_id,
      at: s.created_at,
      by: null,
    });
  }

  return out.sort((a, b) => STANDING_ORDER[a.standing] - STANDING_ORDER[b.standing] || byNewest(a, b));
}

// ── Wording ──────────────────────────────────────────────────────────────────

export const RANK_STANDING_LABEL: Record<RankStanding, string> = {
  official: 'Of record',
  starts_later: 'Starts later',
  replaced: 'Replaced',
  agreed: 'Agreed, not of record yet',
  proposed: 'Proposed, not of record yet',
  session: 'Session rank, not of record',
};

/** A ranking's standing in words: "Starts on Jan 1, 2027" or "Start date pending" for a plan not in force yet. */
export function rankStandingLabel(r: Pick<ItemRanking, 'standing' | 'startsOn'>): string {
  if (r.standing !== 'starts_later') return RANK_STANDING_LABEL[r.standing];
  if (!r.startsOn) return 'Start date pending';
  const d = new Date(`${r.startsOn}T00:00:00`);
  return `Starts on ${Number.isNaN(d.getTime()) ? r.startsOn : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/** `4`, `T2` for a tie, `—` when there is no position. */
export function formatRank(rank: number | null, tied = false): string {
  if (rank == null) return '—';
  return tied ? `T${rank}` : String(rank);
}

export interface ItemRankingSummary {
  /** The item's ranks of record, one per cycle that has a Plan of Record. */
  official: ItemRanking[];
  /** Lowest and highest session rank, or null when no session has ranked it. */
  sessionRange: { low: number; high: number; count: number } | null;
}

export function summarizeItemRankings(rankings: readonly ItemRanking[]): ItemRankingSummary {
  const sessionRanks = rankings
    .filter((r) => r.standing === 'session' && r.rank != null)
    .map((r) => r.rank as number);
  return {
    official: rankings.filter((r) => r.standing === 'official'),
    sessionRange:
      sessionRanks.length === 0
        ? null
        : { low: Math.min(...sessionRanks), high: Math.max(...sessionRanks), count: sessionRanks.length },
  };
}

/**
 * Everywhere an item is being weighed besides the track its priority list came
 * from — the standing strip's Elsewhere line. A track counts once, whether the
 * item sits in its pool or a session in it ranked the item. A session in no
 * track has no track to be counted under, so it counts by itself, under its
 * own name (owner, 2026-10-28: the card said nothing else was considering an
 * item that a track-less session had just ranked #1).
 */
export function elsewhereEvaluations(args: {
  /** Tracks whose item pool holds the item. */
  poolTrackIds: Iterable<string>;
  /** Every session result that ranked the item, with the session's track when it has one. */
  rankedSessions: ReadonlyArray<{ sessionId: string; sessionName: string; trackId: string | null }>;
  /** The track the item's best priority list came from; it is the Priority cell, not Elsewhere. */
  priorityTrackId: string | null;
  trackName: (trackId: string) => string | null | undefined;
}): string[] {
  const tracks = new Set<string>(args.poolTrackIds);
  const trackless = new Map<string, string>();
  for (const s of args.rankedSessions) {
    if (s.trackId) tracks.add(s.trackId);
    else trackless.set(s.sessionId, s.sessionName);
  }
  return [
    ...[...tracks]
      .filter((t) => t !== args.priorityTrackId)
      .map((t) => args.trackName(t))
      .filter((n): n is string => !!n),
    ...trackless.values(),
  ];
}

/**
 * The chip beside a cycle's name on the item page's Prioritization tab. The
 * item's rank of record in the plan built from that cycle comes first; a
 * priority list's place is the chip only while no plan of record holds the
 * item (owner, 2026-10-28: the chip read the list's "Priority #3" while the
 * plan's rank of record sat under its own heading). With neither, the item is
 * being considered there.
 */
export function cycleStandingChip(args: {
  /** The item's rank of record in a plan built from this cycle, when one holds it. */
  ofRecord: ItemRanking | null | undefined;
  /** The item's place on the cycle's most authoritative priority list, when it is on one. */
  list: ItemRanking | null | undefined;
}): { text: string; tone: 'green' | 'mustard' | 'slate' } {
  if (args.ofRecord) return { text: `Plan #${formatRank(args.ofRecord.rank)}`, tone: 'green' };
  if (args.list) return { text: `Priority #${formatRank(args.list.rank, args.list.tied)}`, tone: 'mustard' };
  return { text: 'Being considered', tone: 'slate' };
}
